import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { facilityCoverage, overviewSurvey, surveyLinkCreate, type ApiError, type SurveyLinkMade, type SurveyPart } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { AboutThis } from '../../ui/AboutThis';
import { sectionsForRole } from '../settings/lens';
import { tierLabel } from '../residents/common';
import { coverageCard } from '../worklist/v9';
import { usePerf } from './ctx';
import { pre, usePerfApi } from './lib';
import { NOT_CONNECTED, NOT_CONNECTED_LINE, NotConnected, ReadOnlyLine, SectionHead, V9_BTN, V9_GHOST, V9_HEAD, V9_PANE, V9_PRI, V9_SECTION, v9Dot } from './v9parts';
import { DAY_ONE_DOCS, SURVEY_DAY_TAGS, linkReady, pastHead, pastRows, rbsColor, rbsRows, staffRows, surveyLead, whenCells } from './surveyVals';

/** A forbidden or missing route reads as "not connected", never as an error on screen. */
function allowed<T>(p: Promise<T>): Promise<T | null> {
  return p.catch((e: ApiError) => {
    if (e && (e.kind === 'not_found' || e.kind === 'forbidden')) return null;
    throw e;
  });
}

const ALL_PARTS: SurveyPart[] = ['guardian', 'ledger', 'breakglass', 'medpass', 'staffing'];
const LINK_HOURS = 8;
const GREY = '#54606C';

/**
 * Performance > Survey ready (design ConsoleV9 isSurveyTab, L19047). The Facility Admin acts;
 * Super Admin, Support, the Owner/Operator and Developers read it (the design's survey-day
 * card and pulled-away coverage show to the Facility Admin and the Aithora team only).
 */
export function SurveyReadyTab() {
  const { fac, fa, canAct: ctxAct } = usePerf();
  const { role } = useConsoleRole();
  const { client, session } = useSession();
  const navigate = useNavigate();
  const team = role === 'SUPER_ADMIN' || role === 'OPERATOR';
  const rankShown = team || role === 'PARTNER';
  const sdShown = fa || team;
  const nowMs = Date.now();
  // The Facility Admin's building is pinned by the server; the Aithora team reads the picked one.
  const facId = fa ? session?.user.organizationId ?? null : fac?.orgId ?? null;

  const survey = usePerfApi((c, signal) => allowed(overviewSurvey(c, { query: { facility: fa ? undefined : facId ?? undefined }, signal })), [facId, fa], fa || !!facId);
  const coverage = usePerfApi((c, signal) => allowed(facilityCoverage(c, { params: { id: facId! }, signal })), [facId], sdShown && !!facId);

  const [sdAt, setSdAt] = useState<string | null>(null);
  const [link, setLink] = useState<SurveyLinkMade | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const s = survey.data ?? null;
  const when = whenCells(s, nowMs);
  const past = pastRows(s);
  const staff = staffRows(fac?.metrics);
  const rbs = rbsRows(fac?.metrics, s);
  const { lead, sub } = surveyLead(fa);
  const canBinder = !!role && sectionsForRole(role).includes('Surveyor export');
  const canAct = fa && ctxAct;
  const roText = 'Read-only as ' + tierLabel(role);
  const cov = coverage.data ? coverageCard({ coveredOnTime: coverage.data.coveredOnTime, live: coverage.data.live }) : coverageCard(null);

  const makeLink = async () => {
    if (busy) return;
    setBusy(true);
    setLinkErr(null);
    try {
      setLink(await surveyLinkCreate(client, { body: { range: 'survey', parts: ALL_PARTS, hours: LINK_HOURS } }));
    } catch (e) {
      setLinkErr(e instanceof Error ? e.message : 'The link could not be made.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-screen-label="Performance · Survey ready" data-testid="perf-survey" style={sx(V9_PANE)}>
      <div style={sx('display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;')}>
        <div style={sx('flex:1 1 420px;min-width:0;display:flex;flex-direction:column;gap:3px;')}>
          <div style={sx('font-size:18px;font-weight:700;line-height:1.4;text-wrap:pretty;')}>{lead}</div>
          <div style={sx('font-size:16px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>{sub}</div>
        </div>
        {canBinder && (
          <a
            href="#"
            data-testid="srv-binder"
            onClick={(e) => {
              pre(e);
              navigate('/settings?section=surveyor-export');
            }}
            style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:9px;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
          >
            Prepare the surveyor file
          </a>
        )}
      </div>

      {rankShown && (
        <div data-screen-label="Survey ready · Buildings" data-testid="srv-rank" style={sx(V9_SECTION)}>
          <SectionHead title="Your buildings, highest survey risk first" />
          <NotConnected text="Not connected yet. The ranking needs each building’s open survey items, which are not connected to Aithora yet." />
          {fac?.name && <div style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-top:6px;')}>{'The detail below is ' + fac.name + '.'}</div>}
        </div>
      )}

      <div data-screen-label="Survey ready · Areas" data-testid="srv-areas" style={sx(V9_SECTION)}>
        <SectionHead title="Most-cited areas, worst risk first" />
        <div style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-bottom:4px;max-width:90ch;text-wrap:pretty;')}>
          Surveyors grade each problem by how serious it is (no harm, harm, or immediate danger) and how many residents it touches (one, a pattern, or widespread).
        </div>
        <NotConnected />
      </div>

      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,360px),1fr));gap:16px;align-items:start;')}>
        <div data-testid="srv-staff" style={sx('border-top:1px solid rgba(34,39,46,.14);padding-top:10px;display:flex;flex-direction:column;min-width:0;gap:8px;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
            <span style={sx(V9_HEAD)}>Staffing vs. how much care residents need</span>
          </div>
          {staff.map((r) => (
            <div key={r.k} style={sx('display:flex;flex-direction:column;gap:2px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.08);min-width:0;')}>
              <div style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;')}>
                <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:700;')}>{r.k}</span>
                <span style={sx('font-size:16px;font-variant-numeric:tabular-nums;color:' + (r.v === NOT_CONNECTED ? GREY : '#22272E') + ';')}>{r.v}</span>
                {r.tag && <span style={sx('font-size:14px;font-weight:700;color:' + r.tagColor + ';')}>{r.tag}</span>}
              </div>
              <div style={sx('font-size:14px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>{r.note}</div>
            </div>
          ))}
          <div style={sx('display:flex;flex-direction:column;gap:4px;margin-top:4px;padding:10px 12px;border-radius:9px;background:rgba(47,92,151,.07);')}>
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
              <span style={sx('font-size:16px;font-weight:800;')}>What closes the gap</span>
            </div>
            <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>Not connected yet. Shifts and weekly cost show here once expected hours and pay rates are connected.</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>Source: CMS Payroll-Based Journal, as published on Care Compare.</div>
        </div>

        <div data-testid="srv-when" style={sx('border-top:1px solid rgba(34,39,46,.14);padding-top:10px;display:flex;flex-direction:column;min-width:0;gap:8px;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
            <span style={sx(V9_HEAD)}>When the surveyor may come</span>
          </div>
          {when ? (
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;')}>
              {when.map((w) => (
                <div key={w.k} style={sx('min-width:0;')}>
                  <div style={sx('font-size:14px;color:#54606C;')}>{w.k}</div>
                  <div style={sx('font-size:18px;font-weight:800;font-variant-numeric:tabular-nums;')}>{w.v}</div>
                  <div style={sx('font-size:14px;color:#54606C;line-height:1.35;')}>{w.s}</div>
                </div>
              ))}
            </div>
          ) : (
            <NotConnected
              text={
                survey.loading
                  ? 'Loading…'
                  : !s
                    ? NOT_CONNECTED_LINE
                    : !s.available && s.reason
                      ? s.reason
                      : 'No standard survey date is on file in the CMS catalog for this building.'
              }
            />
          )}
          <AboutThis inline textStyle="font-size:16px;color:#3C454F;line-height:1.45;text-wrap:pretty;">
            Standard surveys come at least every 15 months, about every 12 on average across the state. Complaint visits can come any day. Surveys are unannounced.
          </AboutThis>
          {past.length > 0 && (
            <>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-top:6px;')}>
                <span style={sx(V9_HEAD)}>{pastHead(past.length)}</span>
                <span style={sx('font-size:14px;color:#54606C;')}>CMS Care Compare</span>
              </div>
              {past.map((p) => (
                <div key={p.d + p.kind} style={sx('display:flex;flex-direction:column;gap:1px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.08);')}>
                  <div style={sx('display:flex;gap:4px 10px;flex-wrap:wrap;')}>
                    <span style={sx('font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;')}>{p.d}</span>
                    <span style={sx('font-size:16px;color:#54606C;')}>{p.kind}</span>
                  </div>
                  <span style={sx('font-size:16px;color:#22272E;line-height:1.4;text-wrap:pretty;')}>{p.res}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div data-screen-label="Survey ready · Shorter survey" data-testid="srv-rbs" style={sx('border-top:1px solid rgba(34,39,46,.14);padding-top:10px;display:flex;flex-direction:column;gap:4px;min-width:0;')}>
        <div style={sx('display:flex;align-items:baseline;gap:8px 12px;flex-wrap:wrap;')}>
          <span style={sx(V9_HEAD)}>{rbs.head}</span>
        </div>
        <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;max-width:90ch;text-wrap:pretty;')}>
          Buildings that meet all eleven CMS conditions get the Risk-Based Survey: fewer surveyors, about half the time on site, and a High Performing icon on Care Compare.
        </div>
        {rbs.rows.map((r) => (
          <span key={r.t} style={sx('font-size:16px;line-height:1.4;color:' + rbsColor(r.state) + ';')}>
            {r.t}
          </span>
        ))}
      </div>

      {sdShown && (
        <div data-screen-label="Survey day" data-testid="srv-day" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);min-width:0;')}>
          {!sdAt ? (
            <div style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;')}>
              <div style={sx('flex:1 1 260px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Surveyors here?</span>
                <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>One tap when surveyors arrive: readiness for the top tags, what to fix now, who to brief, and the secure link.</span>
              </div>
              {canAct ? (
                <a
                  href="#"
                  data-testid="srv-day-start"
                  onClick={(e) => {
                    pre(e);
                    setSdAt(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
                  }}
                  style={sx(V9_PRI)}
                >
                  Start survey-day mode
                </a>
              ) : (
                <ReadOnlyLine text={roText} />
              )}
            </div>
          ) : (
            <>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#B3261E;')}>{'Survey day · surveyors arrived at ' + sdAt}</span>
              {SURVEY_DAY_TAGS.map((t) => (
                <div key={t.tag} data-testid="srv-day-tag" style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;padding:5px 0;border-top:1px solid rgba(34,39,46,.06);min-width:0;')}>
                  <span style={sx('flex:0 1 300px;min-width:0;font-size:16px;font-weight:700;color:#22272E;')}>{t.tag + ' · ' + t.name}</span>
                  <span style={sx('flex:1 1 280px;min-width:0;font-size:16px;color:#54606C;')}>{NOT_CONNECTED}</span>
                  <a
                    href="#"
                    onClick={(e) => {
                      pre(e);
                      navigate(t.path);
                    }}
                    style={sx(V9_GHOST)}
                  >
                    Open records
                  </a>
                </div>
              ))}
              <span style={sx('font-size:16px;font-weight:800;color:#22272E;margin-top:4px;')}>Fix now</span>
              <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{NOT_CONNECTED_LINE}</span>
              <div style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:4px;')}>
                <a href="#" aria-disabled="true" title="Not connected yet: the shift brief has no back end." onClick={pre} style={sx(V9_GHOST + 'color:#54606C;cursor:not-allowed;')}>
                  Brief all staff on shift
                </a>
                {!link && (
                  <a
                    href="#"
                    data-testid="srv-day-link"
                    aria-disabled={busy || undefined}
                    onClick={(e) => {
                      pre(e);
                      void makeLink();
                    }}
                    style={sx(V9_GHOST + (busy ? 'opacity:.55;cursor:default;' : ''))}
                  >
                    Create secure surveyor link
                  </a>
                )}
                <a
                  href="#"
                  onClick={(e) => {
                    pre(e);
                    setSdAt(null);
                  }}
                  style={sx(V9_GHOST + 'border-color:transparent;color:#54606C;')}
                >
                  End survey-day mode
                </a>
              </div>
              {link && (
                <div data-testid="srv-day-link-ready" style={sx('display:flex;flex-direction:column;gap:4px;min-width:0;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;line-height:1.4;')}>{linkReady(link.hours)}</span>
                  <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0;')}>
                    <span translate="no" style={sx('font-size:14px;color:#3C454F;overflow-wrap:anywhere;min-width:0;')}>{link.url}</span>
                    <a
                      href="#"
                      onClick={(e) => {
                        pre(e);
                        void navigator.clipboard?.writeText(link.url).catch(() => undefined);
                      }}
                      style={sx(V9_BTN)}
                    >
                      Copy link
                    </a>
                  </span>
                </div>
              )}
              {linkErr && (
                <span role="alert" style={sx('font-size:16px;font-weight:700;color:#B3261E;line-height:1.4;')}>
                  {linkErr}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {sdShown && (
        <div data-screen-label="Survey ready · Pulled-away coverage" data-testid="srv-coverage" style={sx('border-top:1px solid rgba(34,39,46,.14);padding-top:10px;display:flex;flex-direction:column;gap:4px;min-width:0;')}>
          <span style={sx(V9_HEAD)}>Covering when staff are pulled away</span>
          <span style={sx('font-size:18px;font-weight:800;color:#22272E;line-height:1.35;')}>{cov.head}</span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>{cov.sub}</span>
          <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>Included in the surveyor export: every moved task shows who it came from, why, who took it and when it was given.</span>
        </div>
      )}

      <div data-screen-label="Survey ready · Day-one list" data-testid="srv-docs" style={sx(V9_SECTION)}>
        <SectionHead title="What the surveyor asks for on day one" />
        <div style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-bottom:4px;')}>The entrance conference list. Most of it is due within the first hour.</div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr));column-gap:24px;')}>
          {DAY_ONE_DOCS.map((d) => (
            <div key={d.name} style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.08);min-width:0;')}>
              <span style={sx(v9Dot(GREY, false))} />
              <div style={sx('flex:1 1 240px;min-width:0;')}>
                <div style={sx('font-size:16px;font-weight:700;line-height:1.35;')}>{d.name}</div>
                <div style={sx('font-size:14px;color:#54606C;line-height:1.35;')}>{d.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {!fa && !sdShown && <ReadOnlyLine text={roText} />}
    </div>
  );
}
